import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { Button } from '@material-ui/core';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { SORT_ASC, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useFindById, useTags } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import {
    CITY_FIELD,
    COUNTRY_FIELD,
    DESCRIPTION_FIELD,
    IN_SPEED_FIELD,
    IP_RULES_FIELD,
    LATITUDE_FIELD,
    LONGITUDE_FIELD,
    MATCHING_DEVICE_DATA_SOURCE,
    MATCHING_DEVICES_COLUMNS,
    MAX_AUTO_INTERFACES,
    NAME_FIELD,
    NORM_NAME_FIELD,
    NOTES_FIELD,
    NOTIFICATIONS_COLUMNS,
    notificationsEmailDataSource,
    OUT_SPEED_FIELD,
    PROVINCE_FIELD,
    RULES_COLUMNS,
    TAGS_FIELD,
    WAN_INTERFACES_FIELD,
    WAN_META_ADDS_FIELD,
    WAN_META_DELETES_FIELD,
    WAN_META_FIELD,
} from './SiteAdminConstants';
import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import NgpRow from '../../form/NgpRow';
import NgpNumberField from '../../form/NgpNumberField';
import { NgpTagsField } from '../../form/NgpTagsField';
import ListFormField from '../../form/ListFormField';
import IpScopeForm from '../../ip-form/IpScopeForm';
import {
    useMatchingDeviceLoader,
    useSiteHelperService,
    convertToSpeedText,
    convertToSpeedValue,
} from './SiteAdminHelper';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import { SiteAdminAgents } from './SiteAdminAgents';
import { SiteAdminWanInterfaces } from './SiteAdminWanInterfaces';
import { IP_RULE_FIELD } from '../../ip-form/IpFormConstants';
import { siteFilterId } from '../../filters';
import { SITE_TYPE } from '../../matrix/MatrixConfigHelper';
import { removeIds, useWanHelperService } from './SiteAdminWanHelper';
import SiteAdminLocationDialog from './SiteAdminLocationDialog';
import LinkButton from '../../links/LinkButton';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';

const functionName = AdminPages.Sites;
const goToUrl = '..';
const apiUrl = '/ipm/v1/admin/sites/';
const apiTagsUrl = '/ipm/v1/admin/sites/tags';
const defaultValues = { [TAGS_FIELD]: [] };

const reportingLinkConfig = {
    target: 'site-detail',
    mappedFilterParams: {
        [siteFilterId]: SITE_TYPE,
    },
};
const tooManyWanInterfacesError = 'wanInterfaceTooMany';
const emptyRulesGridText = t`No rules defined`;
const addButtonText = t`Add IP Scope`;
const emptyDevicesGridText = t`There are no devices provisioned in this site`;
const defaultVisibleRows = 12;
const initialSortMatchingDevice = [{ property: NORM_NAME_FIELD, direction: SORT_ASC }];
const initialSortRules = [{ property: IP_RULE_FIELD, direction: SORT_ASC }];
const notificationsLocation = '/admin/notifications/site';
const IN_SPEED_TEXT_FIELD = `${IN_SPEED_FIELD}Text`;
const OUT_SPEED_TEXT_FIELD = `${OUT_SPEED_FIELD}Text`;

function prepareSavedData(dataToSave, wanMeta) {
    if (!_.isEmpty(dataToSave[TAGS_FIELD])) {
        _.set(dataToSave, TAGS_FIELD, _.flatten(dataToSave[TAGS_FIELD]));
    }

    _.set(dataToSave, WAN_INTERFACES_FIELD, _.get(dataToSave, WAN_INTERFACES_FIELD, []));
    _.set(dataToSave, WAN_META_FIELD, wanMeta);

    // Convert human-readable speeds to bps speeds
    dataToSave[IN_SPEED_FIELD] = convertToSpeedValue(dataToSave[IN_SPEED_TEXT_FIELD]);
    dataToSave[OUT_SPEED_FIELD] = convertToSpeedValue(dataToSave[OUT_SPEED_TEXT_FIELD]);
    delete dataToSave[IN_SPEED_TEXT_FIELD];
    delete dataToSave[OUT_SPEED_TEXT_FIELD];

    return dataToSave;
}

export default function SiteAdminDetail() {
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, setError, handleSubmit, watch, getValues, setValue } = methods;
    const functionBreadCrumbs = [{ link: goToUrl, text: t`Sites` }];
    const newBreadCrumbText = t`New Site`;
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const rules = watch(IP_RULES_FIELD);
    const watchedSiteName = watch(NAME_FIELD);
    const watchedLatitude = watch(LATITUDE_FIELD);
    const watchedLongitude = watch(LONGITUDE_FIELD);
    const matchingDevicesLoader = useMatchingDeviceLoader(MATCHING_DEVICE_DATA_SOURCE, rules);
    const notificationsLoader = useQueryLoader(notificationsEmailDataSource(recordId), { disableFilterState: true });
    const [agentSelectionIsDirty, setAgentSelectionIsDirty] = useState(false);
    const [siteLocationIsDirty, setSiteLocationIsDirty] = useState(false);
    const [wanSelectionIsDirty, setWANSelectionIsDirty] = useState(false);
    const [rulesSelectionIsDirty, setRulesSelectionIsDirty] = useState(false);
    const { tags: siteTags } = useTags({ url: apiTagsUrl });
    const wanMeta = useRef({ manualAddIds: [], manualDeleteIds: [], message: '' });
    const [wanOverLoaded, setWanOverloaded] = useState(false);
    const wanHelperService = useWanHelperService();
    const siteHelperService = useSiteHelperService();
    const formDeleteService = useFormDeleteService();
    const formSaveService = useFormSaveService();
    const [isIpmDisabled] = useIsIpmDisabled();

    const postFindByIdHandler = (site) => {
        setShowDeleteButton(true);
        site[TAGS_FIELD] = _.map(site[TAGS_FIELD], (tag) => [tag]);
        wanMeta.current = site[WAN_META_FIELD];
        site[IN_SPEED_TEXT_FIELD] = convertToSpeedText(site[IN_SPEED_FIELD]);
        site[OUT_SPEED_TEXT_FIELD] = convertToSpeedText(site[OUT_SPEED_FIELD]);

        return siteHelperService.queryRuleMatching(site[IP_RULES_FIELD]).then(() => {
            if (_.isEmpty(site[WAN_META_FIELD])) {
                return site;
            }

            if (site[WAN_META_FIELD].message !== tooManyWanInterfacesError) {
                return site;
            }

            return wanHelperService
                .queryWanInterfaceConfig()
                .then((config) => {
                    if (!config.autoApply) {
                        return;
                    }

                    return isWanOverLoaded(site);
                })
                .then(() => site);
        });
    };

    const { initialized, finding, formData } = useFindById({
        url: apiUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
    });

    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, NAME_FIELD, newBreadCrumbText, {
        reportingLinkConfig,
    });

    const handleFormSubmit = (dataToSave = {}) => {
        return formSaveService.save({
            url: apiUrl,
            recordId: formData._id,
            dataToSave: _.extend(prepareSavedData(dataToSave, wanMeta.current), {
                _id: formData._id,
            }),
            setError,
        });
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(apiUrl, recordId),
            onDeleteGoTo: goToUrl,
        })();
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    const dirtyCheck = (isDirty) =>
        isDirty || rulesSelectionIsDirty || agentSelectionIsDirty || wanSelectionIsDirty || siteLocationIsDirty;

    const handleSetLocation = (location = {}) => {
        if (
            getValues(LATITUDE_FIELD) === _.get(location, LATITUDE_FIELD, '') &&
            getValues(LONGITUDE_FIELD) === _.get(location, LONGITUDE_FIELD, '')
        ) {
            return;
        }

        setValue(LATITUDE_FIELD, _.get(location, LATITUDE_FIELD, ''));
        setValue(LONGITUDE_FIELD, _.get(location, LONGITUDE_FIELD, ''));
        setValue(CITY_FIELD, _.get(location, CITY_FIELD, ''));
        setValue(PROVINCE_FIELD, _.get(location, PROVINCE_FIELD, ''));
        setValue(COUNTRY_FIELD, _.get(location, COUNTRY_FIELD, ''));
        setSiteLocationIsDirty(true);
    };

    const handleSpeedChange = (event, fieldName) => {
        if (event.target.value && !/^[0-9]+[KMGT]?$/.test(event.target.value)) {
            // Only allow valid number[+suffix] values to be entered
            const value = event.target.value
                .replace(/[^0-9KMGT]/g, '')
                .replace(/^[KGMT]+/g, '')
                .replace(/([KGMT])[0-9KGMT]+/g, '$1');
            setValue(fieldName, value);
        }
    };

    const isWanOverLoaded = (site) => {
        return wanHelperService
            .queryWanInterfaceIds(site[IP_RULES_FIELD])
            .then(({ ids }) => wanOverloadedTest(ids, site));
    };

    const wanOverloadedTest = (ids, site) => {
        let totalIds = removeIds(ids, _.get(site, `${WAN_META_FIELD}.${WAN_META_DELETES_FIELD}`));
        totalIds = removeIds(totalIds, _.get(site, `${WAN_META_FIELD}.${WAN_META_ADDS_FIELD}`));

        if (totalIds.length > MAX_AUTO_INTERFACES) {
            setWanOverloaded(true);
            return { wanIds: totalIds, isOverloaded: true };
        }

        setWanOverloaded(false);
        return { wanIds: totalIds, isOverloaded: false };
    };

    function removeAutoInterfaces() {
        setValue(
            WAN_INTERFACES_FIELD,
            removeIds(
                getValues(WAN_INTERFACES_FIELD),
                _.difference(getValues(`${WAN_META_FIELD}${WAN_META_ADDS_FIELD}`), getValues(WAN_INTERFACES_FIELD))
            )
        );
    }

    const refreshWanAndWanMeta = (selectedIds, isManual) => {
        setValue(WAN_INTERFACES_FIELD, _.uniq([...(getValues(WAN_INTERFACES_FIELD) || []), ...selectedIds]));

        if (isManual) {
            const existingWanMeta = wanMeta.current;
            wanMeta.current = {
                ...existingWanMeta,
                [WAN_META_DELETES_FIELD]: removeIds(existingWanMeta[WAN_META_DELETES_FIELD], selectedIds),
                [WAN_META_ADDS_FIELD]: _.uniq([...existingWanMeta[WAN_META_ADDS_FIELD], ...selectedIds]),
            };
        }
    };

    const updateWanInterfaceIds = (site) => {
        return isWanOverLoaded(site).then(({ wanIds, isOverloaded }) => {
            const currentWanInterfaces = getValues(WAN_INTERFACES_FIELD);
            if (isOverloaded) {
                return removeAutoInterfaces();
            }

            if (!_.isEmpty(currentWanInterfaces)) {
                removeAutoInterfaces();
                if (wanIds.length > 0) {
                    refreshWanAndWanMeta(wanIds, false);
                }

                return;
            }

            refreshWanAndWanMeta(wanIds, false);
        });
    };

    const handleWanInterfacesChange = (wanSelection = []) => {
        if (wanSelection.length < (getValues(WAN_INTERFACES_FIELD) || []).length) {
            return handleWanInterfacesRemove(removeIds(getValues(WAN_INTERFACES_FIELD), wanSelection));
        }

        return refreshWanAndWanMeta(removeIds(wanSelection, getValues(WAN_INTERFACES_FIELD)), true);
    };

    const handleWanInterfacesRemove = (removes) => {
        setValue(WAN_INTERFACES_FIELD, removeIds(getValues(WAN_INTERFACES_FIELD), removes));

        const existingWanMeta = wanMeta.current;
        wanMeta.current = {
            ...existingWanMeta,
            [WAN_META_DELETES_FIELD]: _.uniq([...existingWanMeta[WAN_META_DELETES_FIELD], ...removes]),
            [WAN_META_ADDS_FIELD]: removeIds(existingWanMeta[WAN_META_ADDS_FIELD], removes),
        };
    };

    const cleanupOrphanedInterfaces = () => {
        if (
            _.isEmpty(getValues(WAN_INTERFACES_FIELD)) &&
            _.isEmpty(_.get(wanMeta, `current.${WAN_META_DELETES_FIELD}`))
        ) {
            return Promise.resolve();
        }

        return siteHelperService
            .findOrphanedInterfaces(rules, [
                ...(getValues(WAN_INTERFACES_FIELD) || []),
                ..._.get(wanMeta, `current.${WAN_META_DELETES_FIELD}`),
            ])
            .then((orphanedInterfaces) => {
                setValue(WAN_INTERFACES_FIELD, removeIds(getValues(WAN_INTERFACES_FIELD), orphanedInterfaces));

                const existingWanMeta = wanMeta.current;
                wanMeta.current = {
                    ...existingWanMeta,
                    [WAN_META_DELETES_FIELD]: removeIds(existingWanMeta[WAN_META_DELETES_FIELD], orphanedInterfaces),
                    [WAN_META_ADDS_FIELD]: removeIds(existingWanMeta[WAN_META_ADDS_FIELD], orphanedInterfaces),
                };
            });
    };

    const handleWanInterfaces = () => {
        return cleanupOrphanedInterfaces().then(() => {
            if (_.isEmpty(rules)) {
                removeAutoInterfaces();
                setWanOverloaded(false);
            }

            return updateWanInterfaceIds({
                [IP_RULES_FIELD]: rules,
                [WAN_META_FIELD]: wanMeta.current,
            });
        });
    };

    useEffect(() => {
        handleWanInterfaces(rules);
        // too many renders if I add the handleWanInterfaces dep
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rules]);

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {showDeleteButton && (
                        <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                    )}
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting}
                    isDirtyFunc={dirtyCheck}
                >
                    <FormSection title={<Trans>Site Detail</Trans>}>
                        <NgpTextField
                            fieldName={NAME_FIELD}
                            label={<Trans>Name</Trans>}
                            required
                            inputProps={{ size: 35, maxLength: 128 }}
                        />

                        <NgpTextarea
                            fieldName={DESCRIPTION_FIELD}
                            label={<Trans>Description</Trans>}
                            cols={50}
                            inputProps={{ maxLength: 225 }}
                        />

                        <NgpRow>
                            <NgpNumberField
                                fieldName={LATITUDE_FIELD}
                                label={<Trans>Latitude</Trans>}
                                allowDecimals
                                min="-90"
                                max="90"
                            />

                            <NgpNumberField
                                fieldName={LONGITUDE_FIELD}
                                label={<Trans>Longitude</Trans>}
                                allowDecimals
                                min="-180"
                                max="180"
                            />

                            <SiteAdminLocationDialog
                                defaultSearchText={watchedSiteName}
                                siteLatitude={watchedLatitude}
                                siteLongitude={watchedLongitude}
                                onSetLocation={handleSetLocation}
                            />
                        </NgpRow>

                        <NgpRow>
                            <NgpTextField
                                fieldName={CITY_FIELD}
                                label={<Trans>City</Trans>}
                                inputProps={{ size: 35, maxLength: 225 }}
                            />

                            <NgpTextField
                                fieldName={PROVINCE_FIELD}
                                label={<Trans>State</Trans>}
                                inputProps={{ size: 35, maxLength: 225 }}
                            />

                            <NgpTextField
                                fieldName={COUNTRY_FIELD}
                                label={<Trans>Country</Trans>}
                                inputProps={{ size: 35, maxLength: 225 }}
                            />
                        </NgpRow>

                        <NgpRow>
                            <NgpTextField
                                fieldName={IN_SPEED_TEXT_FIELD}
                                label={<Trans>Inward Speed (bit/s)</Trans>}
                                onChangeFunc={(e) => handleSpeedChange(e, IN_SPEED_TEXT_FIELD)}
                            />

                            <NgpTextField
                                fieldName={OUT_SPEED_TEXT_FIELD}
                                label={<Trans>Outward Speed (bit/s)</Trans>}
                                helpTipText={<Trans>Override site inward/outward speed</Trans>}
                                onChangeFunc={(e) => handleSpeedChange(e, OUT_SPEED_TEXT_FIELD)}
                            />
                        </NgpRow>

                        <NgpTextarea
                            fieldName={NOTES_FIELD}
                            label={<Trans>Notes</Trans>}
                            cols={100}
                            inputProps={{ maxLength: 900 }}
                        />

                        <NgpTagsField fieldName={TAGS_FIELD} label={<Trans>Tags</Trans>} options={siteTags} />
                    </FormSection>

                    {!isIpmDisabled && (
                        <FormSection title={t`IP Scopes`} addSpacer>
                            <ListFormField
                                fieldName={IP_RULES_FIELD}
                                dbSelected={_.get(formData, IP_RULES_FIELD)}
                                addButtonText={addButtonText}
                                gridColumns={RULES_COLUMNS}
                                emptyGridText={emptyRulesGridText}
                                formComponent={<IpScopeForm />}
                                postConfirmHandler={siteHelperService.queryRuleMatching}
                                selectedListSorter={initialSortRules}
                                setSelectionIsDirty={setRulesSelectionIsDirty}
                            />
                        </FormSection>
                    )}

                    {!isIpmDisabled && (
                        <SiteAdminWanInterfaces
                            formData={formData}
                            setAgentSelectionIsDirty={setWANSelectionIsDirty}
                            overloaded={wanOverLoaded}
                            wanMeta={wanMeta.current}
                            updateSelectionFunction={handleWanInterfacesChange}
                        />
                    )}

                    <SiteAdminAgents formData={formData} setAgentSelectionIsDirty={setAgentSelectionIsDirty} />

                    {!isIpmDisabled && (
                        <FormSection title={t`Monitored Devices`} addSpacer>
                            <DataTable
                                tableId="Site Monitored Devices"
                                loader={matchingDevicesLoader}
                                columns={MATCHING_DEVICES_COLUMNS}
                                sorters={initialSortMatchingDevice}
                                disableCSVExport
                                disableColumnRemover
                                visibleRows={defaultVisibleRows}
                                emptyText={emptyDevicesGridText}
                            />
                        </FormSection>
                    )}

                    {showDeleteButton && (
                        <FormSection title={t`Notifications`} addSpacer>
                            <div>
                                <LinkButton
                                    label={<Trans>Manage All Notifications</Trans>}
                                    newTab
                                    url={notificationsLocation}
                                />
                            </div>
                            <DataTable
                                loader={notificationsLoader}
                                columns={NOTIFICATIONS_COLUMNS}
                                disableCSVExport
                                disableColumnRemover
                                visibleRows={defaultVisibleRows}
                            />
                        </FormSection>
                    )}
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
